import { SecureRoute } from '@okta/okta-react';
import React, { FC, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { Dashboard as DashboardT } from 'api/dashboard';
import { PageLoading } from 'components/atoms/loading';
import { Page } from 'components/molecules/page';
import {
  DashboardHeader,
  lastVisitedDashboardActions,
  useDashboardInfo,
} from 'components/organisms/dashboard';
import { RoutePaths } from 'constant';
import {
  DASHBOARDS_QUERY_KEY,
  useDashboardsQuery,
  useInitDashboardMutation,
} from 'queries/dashboard';
import { useGetDashboardUrlWithSearch } from 'utils/urls';
import { DashboardById } from './dashboard-by-id';

const Header = styled(Page.Header)`
  padding: var(--spacing-03) var(--layout-01) !important;
  padding-left: var(--left-spacing) !important;
`;

export const DashboardReportPage: FC = () => {
  const queryClient = useQueryClient();
  const { mutate, isLoading: isCreating } = useInitDashboardMutation();
  const { isLoading, data = [] } = useDashboardsQuery({
    onSuccess: (res) => {
      if (res.length === 0) {
        mutate(
          {},
          {
            onSuccess: async ({ dashboard }) => {
              lastVisitedDashboardActions.update(dashboard[0].dashboardId);
              await queryClient.setQueryData<DashboardT[]>(
                DASHBOARDS_QUERY_KEY,
                (old = []) => [...old, ...dashboard],
              );
            },
          },
        );
      }
    },
  });

  const history = useHistory();
  const {
    selectedDashboard: { selectedId },
  } = useDashboardInfo();
  const getDashboardUrl = useGetDashboardUrlWithSearch();

  useEffect(() => {
    if (!data.length) {
      return;
    }
    if (selectedId !== -1) {
      return;
    }
    const lastVisitedDashboardId =
      lastVisitedDashboardActions.getOnlyActive(data);

    if (lastVisitedDashboardId) {
      history.replace(getDashboardUrl(lastVisitedDashboardId));

      return;
    }
    const { dashboardId } = data[0];

    history.replace(getDashboardUrl(dashboardId));
  }, [data, getDashboardUrl, history, selectedId]);

  return (
    <Page $gray>
      <Header sticky title={<DashboardHeader />} />
      <Page.Content>
        {isLoading || isCreating ? (
          <PageLoading />
        ) : data.length > 0 ? (
          <SecureRoute
            path={RoutePaths.dashboardById}
            component={DashboardById}
          />
        ) : null}
      </Page.Content>
    </Page>
  );
};
